import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Scale } from "lucide-react";
import { toast } from "sonner";
import { operasyonHub } from "@/lib/api";
import { akilUltraServicesAction } from "@/lib/akilApi";

export default function PazarlikMerkezPage() {
    const [items, setItems] = useState([]);
    const [bekleyen, setBekleyen] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await operasyonHub.pazarlik();
            setItems(r.items || []);
            setBekleyen(r.bekleyen || 0);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const uretStrateji = async (dosyaId) => {
        try {
            await akilUltraServicesAction({ action: "negotiation_strategy", dosya_id: dosyaId });
            toast.success("Pazarlık stratejisi üretildi");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Üretilemedi");
        }
    };

    return (
        <div className="max-w-screen-2xl mx-auto space-y-6 pb-16 px-4">
            <header className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Akıl Pazarlık</p>
                    <h1 className="font-display text-3xl text-white mt-1">Pazarlık & Uzlaşma Merkezi</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Sigorta tekliflerine karşı önerilen tutar ve cevap metinleri
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="h-10 px-4 rounded-xl bg-[#D4AF37] text-slate-900 text-sm font-bold inline-flex items-center gap-2"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    Yenile
                </button>
            </header>

            {bekleyen > 0 ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-200 text-sm">
                    {bekleyen} pazarlık önerisi onay bekliyor
                </div>
            ) : null}

            {loading ? (
                <div className="flex justify-center py-20">
                    <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                </div>
            ) : !items.length ? (
                <p className="text-center text-slate-500 py-16">Henüz pazarlık kaydı yok</p>
            ) : (
                <div className="space-y-3">
                    {items.map((p) => (
                        <article
                            key={p.id}
                            className="rounded-2xl border border-white/[0.08] bg-[#0a1018]/70 overflow-hidden"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-white/[0.06]">
                                <div>
                                    <Link
                                        to={`/e/sigorta-dosyalari/${p.dosya_id}`}
                                        className="font-mono text-sm text-[#D4AF37] hover:underline"
                                    >
                                        {p.dosya_no}
                                    </Link>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {p.muvekkil} · {p.plaka} · {p.sigorta_sirketi}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${
                                            p.durum === "ONAY_BEKLIYOR"
                                                ? "bg-amber-500/20 text-amber-300"
                                                : "bg-white/10 text-slate-400"
                                        }`}
                                    >
                                        {p.durum}
                                    </span>
                                    <span className="text-lg font-bold text-[#F3E5AB] tabular-nums">
                                        ₺{Number(p.tavsiye_edilen_teklif || 0).toLocaleString("tr-TR")}
                                    </span>
                                </div>
                            </div>
                            <div className="px-4 py-3">
                                <button
                                    type="button"
                                    onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                                    className="text-xs text-slate-400 hover:text-white"
                                >
                                    {expanded === p.id ? "Gizle" : "Strateji & teklif metni"}
                                </button>
                                {expanded === p.id ? (
                                    <div className="mt-3 space-y-2">
                                        {p.analiz_ve_strateji ? (
                                            <pre className="text-xs text-slate-400 whitespace-pre-wrap max-h-40 overflow-y-auto bg-black/30 rounded-lg p-3">
                                                {p.analiz_ve_strateji}
                                            </pre>
                                        ) : null}
                                        {p.teklif_metni ? (
                                            <pre className="text-xs text-[#F3E5AB]/90 whitespace-pre-wrap max-h-32 overflow-y-auto bg-[#D4AF37]/5 rounded-lg p-3">
                                                {p.teklif_metni}
                                            </pre>
                                        ) : null}
                                    </div>
                                ) : null}
                                <div className="flex gap-2 mt-3">
                                    <button
                                        type="button"
                                        onClick={() => uretStrateji(p.dosya_id)}
                                        className="text-[11px] font-semibold text-violet-300 hover:underline inline-flex items-center gap-1"
                                    >
                                        <Scale className="h-3 w-3" />
                                        Yeniden üret
                                    </button>
                                    <Link
                                        to="/yeni/supreme-ops"
                                        className="text-[11px] font-semibold text-emerald-400 hover:underline"
                                    >
                                        Onay kapısı →
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
